import {
  Businesses,
  WaiverRequests,
  WaiverRequestSessions,
} from '@prisma/client';
import qs from 'qs';
import client from '../utils/react-query/client';
import API_URL from '../apiconfig';
import {
  PatchWaiverRequestPayload,
  GetWaiverRequestsResponse,
  PublishWaiversResponse,
  UpdateWaiverPayload,
  UpdateWaiverResponse,
  WaiverSettingsType,
  CreateWaiverPayload,
  CreateWaiverResponse,
  SortableKeys,
  GetWaiverRequestResponse,
  SubmitEmployeeWaiverPayload,
  GetWaiversData,
  AddIDToWaiverRequestPayload,
  AddIDToWaiverRequestResponse,
  WaiverSubmissionStatus,
} from '../types/waivers';
import axios from './api';
import { uploadFiles } from './global';
import {
  cleanupFileName,
  postMessageToReactNative,
} from '../utils/global/helper';
import { Appointment } from '../interfaces';
import { SortValues } from '../types/ui';
import { CustomerMessagePayload } from '../types/global';

export async function fetchWaivers({ businessId }: { businessId: string }) {
  try {
    const waivers = await axios.get(`${API_URL}/v1/waivers/v2`, {
      params: {
        businessId,
      },
    });

    return waivers.data.result as GetWaiversData[];
  } catch (error) {
    throw new Error('Cannot get subscription', { cause: error });
  }
}

export async function fetchWaiverRequests({
  businessId,
  page,
  sort,
  waiverId,
  statuses,
  customerId,
}: {
  businessId: string;
  page: number;
  sort?: SortValues<SortableKeys>;
  waiverId?: string;
  statuses?: WaiverSubmissionStatus[];
  customerId?: string;
}) {
  try {
    const waiverRequests = await axios.get(
      `${API_URL}/v1/waivers/waiver-requests`,
      {
        params: {
          businessId,
          page,
          sortOrderBy: sort?.orderBy,
          sortOrder: sort?.order,
          waiverId,
          statuses,
          customerId,
        },
        paramsSerializer: params =>
          qs.stringify(params, {
            arrayFormat: 'repeat',
            allowEmptyArrays: true,
          }),
      },
    );

    return waiverRequests.data.result as GetWaiverRequestsResponse;
  } catch (error) {
    throw new Error('Cannot get waiver requests', { cause: error });
  }
}

export async function fetchWaiverRequest({
  waiverRequestId,
}: {
  waiverRequestId: string;
}) {
  try {
    const waiverRequest = await axios.get(
      `${API_URL}/v1/waivers/waiver-requests/${waiverRequestId}`,
    );

    return waiverRequest.data.result as GetWaiverRequestResponse;
  } catch (error) {
    throw new Error('Cannot get waiver request', { cause: error });
  }
}

export async function publishWaiver(draftWaiverId: string, businessId: string) {
  try {
    const waivers = await axios.patch(
      `${API_URL}/v1/waivers/${draftWaiverId}/publish`,
      {
        businessId,
        applyToAppointments: true,
      },
    );

    return waivers.data.result as PublishWaiversResponse;
  } catch (error) {
    throw new Error('Cannot publish waiver', { cause: error });
  }
}

export async function updateWaiver(
  draftWaiverId: string,
  payload: UpdateWaiverPayload,
) {
  const waivers = await axios.patch(
    `${API_URL}/v1/waivers/${draftWaiverId}`,
    payload,
  );

  return waivers.data.result as UpdateWaiverResponse;
}

export async function createWaiver(payload: CreateWaiverPayload) {
  const waivers = await axios.post(`${API_URL}/v1/waivers`, payload);

  return waivers.data.result as CreateWaiverResponse;
}

export async function cancelWaiverRequest(payload: PatchWaiverRequestPayload) {
  try {
    const waivers = await axios.patch(`${API_URL}/v1/waivers/cancel`, payload);

    return waivers.data.result;
  } catch (error) {
    throw new Error('Cannot cancel waiver', { cause: error });
  }
}

export async function reactivateWaiverRequest(
  payload: PatchWaiverRequestPayload,
) {
  try {
    const waivers = await axios.patch(
      `${API_URL}/v1/waivers/reactivate`,
      payload,
    );

    return waivers.data.result;
  } catch (error) {
    throw new Error('Cannot reactivate waiver', { cause: error });
  }
}

export async function sendWaiverReminder(
  payload: PatchWaiverRequestPayload & CustomerMessagePayload,
) {
  try {
    const waivers = await axios.patch(`${API_URL}/v1/waivers/remind`, payload);

    return waivers.data.result;
  } catch (error) {
    throw new Error('Cannot send reminder', { cause: error });
  }
}

export async function createWaiverRequestSession(
  payload: PatchWaiverRequestPayload,
) {
  try {
    const waivers = await axios.post(`${API_URL}/v1/waivers/session`, payload);

    return waivers.data.result as {
      session: WaiverRequestSessions;
      business: Pick<Businesses, 'urlPath'>;
    };
  } catch {
    throw new Error('Cannot create waiver request session');
  }
}

export function getWaiverDocumentPath({
  businessId,
  name,
  fileName,
  type,
}: {
  businessId: string;
  name: string;
  fileName: string;
  type: WaiverSettingsType;
}) {
  return `waivers/${businessId}/all/${name}/settings/${type}/${cleanupFileName(
    fileName,
  )}`;
}

export async function uploadWaiverDocument({
  businessId,
  name,
  file,
  type,
}: {
  businessId: string;
  name: string;
  file: File;
  type: WaiverSettingsType;
}) {
  if (!(file instanceof File)) return null;
  const path = getWaiverDocumentPath({
    businessId,
    name,
    fileName: file.name,
    type,
  });
  const result = await uploadFiles([
    {
      id: 'document',
      path,
      url: URL.createObjectURL(file),
    },
  ]).catch(() => [null]);
  return result?.[0] ?? null;
}

export async function archiveWaiver(waiverId: string, businessId: string) {
  try {
    await axios.patch(`${API_URL}/v1/waivers/${waiverId}/archive`, {
      businessId,
    });
    return true;
  } catch (error) {
    throw new Error('Cannot archive waiver', { cause: error });
  }
}

export async function submitEmployeeWaiver(
  payload: SubmitEmployeeWaiverPayload,
) {
  try {
    const waivers = await axios.post(
      `${API_URL}/v1/waivers/waiver-requests/${payload.waiverRequestId}/submit-employee`,
      payload,
    );
    return waivers.data.result as WaiverRequests;
  } catch (error) {
    throw new Error('Cannot submit employee waiver', { cause: error });
  }
}

export async function addIDToWaiverRequest(
  payload: AddIDToWaiverRequestPayload,
) {
  try {
    const waivers = await axios.post(
      `${API_URL}/v1/waivers/waiver-requests/${payload.waiverRequestId}/id`,
      payload,
    );
    return waivers.data.result as AddIDToWaiverRequestResponse;
  } catch (error) {
    throw new Error('Cannot add ID to waiver request', { cause: error });
  }
}

export async function invalidateWaiverRequests() {
  await client.invalidateQueries({ queryKey: ['waiver-requests'] });
}

export async function requestWaiver({
  appointment,
}: {
  appointment: Pick<Appointment, 'appointmentId'>;
}) {
  try {
    const waivers = await axios.patch(`${API_URL}/v1/waivers/request`, {
      appointmentId: appointment.appointmentId,
    });

    return waivers.data.result;
  } catch (error) {
    throw new Error('Cannot request waiver', { cause: error });
  }
}

export async function invalidateWaivers() {
  postMessageToReactNative('waivers-updated');
  await client.invalidateQueries({ queryKey: ['waivers'], exact: false });
}
